import React, { useEffect, useMemo, useRef, useState } from 'react';
import idConfig from 'utils/ecommerce/services/id.config';
import ServicesAccordion from '../common/ServicesAccordion';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import Button from 'components/CustomButtons/Button';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { NumbersService } from 'services/numbers/numbers';
import { translate } from 'components/Translation';
import { capitalize } from 'utils/converters/capitalize';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/servicesStyle';
import { makeStyles } from '@material-ui/core';
import { Caption, BodyS, HeadingSBold } from 'components/Typography/Typography';
import ChangeCreditLimitDialog from 'views/TopUpAddOns/commons/ChangeCreditLimitDialog';
import CpnAccordionContentWrapper from './subcomponents/CpnAccordionContentWrapper';
import { MAX_CREDIT_LIMIT, MIN_CREDIT_LIMIT } from 'utils/const/services';
import GobizAccordionContentWrapper from './subcomponents/GobizAccordionContentWrapper';
import MobileServicesContentWrapper from './subcomponents/MobileServicesContentWrapper';
import JawazakContentWrapper from './subcomponents/JawazakContentWrapper';
import InternationalMinutesAccordionWrapper from './subcomponents/InternationalMinutesAccordionWrapper';
import MobileInternetPlansContentWrapper from './subcomponents/MobileInternetPlansContentWrapper';
import { Package } from 'utils/api/models/numbers/usage';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import { CustomAttribute, extractAttributeValue } from 'utils/ecommerce/services/extract';
import { useLocation } from 'react-router';
import { getProductId } from 'views/Numbers/Details/Services/helpers/getProductId';
import { SingleSubscriberProduct } from 'utils/api/models/subscriberProducts/singleSubscriberProduct';
import { SingleOrderStatus } from 'utils/api/models/orderTracking/singleOrderStatus';
import apiConfig from 'utils/api/config';
import { SinglePackage } from 'utils/api/models/numbers/singlePackage';
import invalidateCache from 'utils/api/invalidateCache';
import { AuthConsumer } from 'components/Auth/authContext';

// @ts-ignore
const useStyles = makeStyles(styles);

const useQuery = () => {
	return new URLSearchParams(useLocation().search);
};

const ACTIVE_SECTION_DEFAULT_VALUE = '1';

interface IMobilePostpaidProps {
	subscriberProducts: SingleSubscriberProduct[];
	showOnlyActive: boolean;
	phoneNumber: string;
	accountType: string;
	billingType: string;
	accountNumber: string;
	userData?: CorpUserData;
	updateOrderItems: types.services.updateOrderItemsFunction;
	packages: Package[];
	tariffPlan: SingleSubscriberProduct | undefined;
	orderStatuses: SingleOrderStatus[];
	documentId?: string;
}

const MobilePostpaid = ({
	showOnlyActive,
	phoneNumber,
	accountType,
	billingType,
	accountNumber,
	userData,
	updateOrderItems,
	packages,
	tariffPlan,
	subscriberProducts,
	orderStatuses,
	documentId,
}: IMobilePostpaidProps) => {
	const classes = useStyles();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);
	const [creditLimit, setCreditLimit] = useState<string>('');
	const [pendingStatuses, setPendingStatuses] = useState<types.services.IPendingPostpaidServices>({
		jawazak: false,
		gobiz: false,
		internationalMinutes: false,
		mobileInternetPlans: false,
	});
	const [defaultRoamingPayG, setDefaultRoamingPayG] = useState<{ id: string; place: string }>({
		id: idConfig.PAYG.legacyProductId,
		place: idConfig.PAYG.place,
	});

	const [isMobileServicesActive, setIsMobileServicesActive] = useState<boolean>(false);
	const [isMobileInternetPlansActive, setIsMobileInternetPlansActive] = useState<boolean>(false);
	const [isCpnActive, setIsCpnActive] = useState<boolean>(false);
	const [isGoBizActive, setIsGoBizActive] = useState<boolean>(false);
	const [isJawazakActive, setIsJawazakActive] = useState<boolean>(false);
	const [isInternationalMinutesActive, setIsInternationalMinutesActive] = useState<boolean>(false);

	const accountDetails: string = `${capitalize(accountType)} - ${capitalize(billingType)}`;
	const isMounted = useRef(true);
	const dispatch = useDispatch();
	const query = useQuery();
	const activeSection = parseInt(query.get('active') || ACTIVE_SECTION_DEFAULT_VALUE);
	const [defaultPayG, setDefaultPayG] = useState<{ id: string; place: string }>({
		id: idConfig.PAY_AS_YOU_GO.legacyProductId,
		place: idConfig.PAY_AS_YOU_GO.place,
	});

	useEffect(() => {
		const id = tariffPlan?.productId;
		let defaultPayGId: string,
			defaultPayGPlace: string,
			defaultRoamingPayGId: string,
			defaultRoamingPayGPlace: string;
		if (id) {
			ECommerceService.getContentByPartNumber(id).subscribe(({ response }) => {
				if (response.catalogEntryView && response.catalogEntryView.length > 0) {
					// if there is more than one element in catalogEntryView array
					// we assume that the configuration is wrong and take the first item
					const catalogEntryView = response.catalogEntryView[0];
					defaultPayGId =
						extractAttributeValue(
							{ attributes: catalogEntryView.attributes },
							CustomAttribute.DEFAULT_PAYG
						) || idConfig.PAY_AS_YOU_GO.legacyProductId;
					defaultRoamingPayGId =
						extractAttributeValue(
							{ attributes: catalogEntryView.attributes },
							CustomAttribute.DEFAULT_ROAMING_PAYG
						) || idConfig.PAYG.legacyProductId;
					const definitionSource = extractAttributeValue(
						{ attributes: catalogEntryView.attributes },
						CustomAttribute.DEFINITION_SOURCE
					);
					defaultPayGPlace = definitionSource || idConfig.PAY_AS_YOU_GO.place;
					defaultRoamingPayGPlace = definitionSource || idConfig.PAYG.place;
					setDefaultPayG({
						id: defaultPayGId,
						place: defaultPayGPlace,
					});
					setDefaultRoamingPayG({
						id: defaultRoamingPayGId,
						place: defaultRoamingPayGPlace,
					});
				}
			});
		}
	}, [tariffPlan]);

	const roamingProduct: SingleSubscriberProduct | undefined = useMemo(
		() => subscriberProducts.find(({ productId }) => productId === getProductId(idConfig.ROAMING.legacyProductId)),
		[subscriberProducts]
	);

	const roamingOrderStatus: SingleOrderStatus | undefined = useMemo(
		() => orderStatuses.find(({ productId }) => productId === getProductId(idConfig.ROAMING.legacyProductId)),
		[orderStatuses]
	);

	const internationalProduct: SingleSubscriberProduct | undefined = useMemo(
		() =>
			subscriberProducts.find(
				({ productId }) => productId === getProductId(idConfig.INTERNATIONAL.legacyProductId)
			),
		[subscriberProducts]
	);

	const internationalOrderStatus: SingleOrderStatus | undefined = useMemo(
		() => orderStatuses.find(({ productId }) => productId === getProductId(idConfig.INTERNATIONAL.legacyProductId)),
		[orderStatuses]
	);
	const roamingInternationalEnabled: boolean =
		roamingProduct?.status === 'Active' &&
		internationalProduct?.status === 'Active' &&
		((!!roamingOrderStatus && !!internationalOrderStatus) || (!roamingOrderStatus && !internationalOrderStatus));

	const roamingInternationalPending: boolean =
		(!!roamingOrderStatus && !!internationalOrderStatus) ||
		(!roamingProduct && internationalProduct?.status === 'Active' && !!roamingOrderStatus);

	const internationalPending: boolean = useMemo(() => {
		return (
			(!!internationalOrderStatus && !roamingOrderStatus) ||
			(internationalProduct?.status === 'Active' &&
				roamingProduct?.status === 'Active' &&
				!!roamingOrderStatus &&
				!internationalOrderStatus)
		);
		// tuning on when international + roaming was active
	}, [internationalProduct, internationalOrderStatus, roamingOrderStatus, roamingProduct]);

	const internationalEnabled: boolean = internationalProduct?.status === 'Active' && !roamingProduct;

	const roamingPending: boolean = !!roamingOrderStatus;

	const roamingEnabled: boolean = !!roamingProduct && roamingProduct.status === 'Active';

	const roamingInternationalPendingStatusDate = useMemo(() => {
		const filteredSubscriberProducts = subscriberProducts.filter(
			({ productId, status }) =>
				status !== 'Active' &&
				(getProductId(productId) === getProductId(idConfig.INTERNATIONAL.legacyProductId) ||
					getProductId(productId) === getProductId(idConfig.ROAMING.legacyProductId))
		);
		if (filteredSubscriberProducts.length > 0) {
			return filteredSubscriberProducts[0].startDate;
		}
		const filteredOrderStatuses = orderStatuses.filter(
			({ productId }) =>
				getProductId(productId) === getProductId(idConfig.INTERNATIONAL.legacyProductId) ||
				getProductId(productId) === getProductId(idConfig.ROAMING.legacyProductId)
		);
		return filteredOrderStatuses.length > 0 ? filteredOrderStatuses[0].startDate : '';
	}, [subscriberProducts, orderStatuses]);

	const setPendingStatus = (status: boolean, type: keyof types.services.IPendingPostpaidServices) => {
		setPendingStatuses((prevStatus) => ({ ...prevStatus, [type]: status }));
	};

	const updateServices = (
		orderItems: types.services.IOrderItem[],
		name: string,
		action: types.services.actionType,
		asynchronousActivation?: boolean
	) => {
		updateOrderItems(orderItems, action, name, asynchronousActivation);
	};

	useEffect(() => {
		setLoading(true);
		dispatch(showLoadingIndicator());
		invalidateCache(['getCreditLimit']);
		NumbersService.getCreditLimit({
			phoneNumber,
			accountNumber,
		})
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					if (isMounted.current) {
						setLoading(false);
					}
				})
			)
			.subscribe(
				({ productCharacteristic, result }) => {
					if (result.resultDescription === 'Success' && productCharacteristic && isMounted.current) {
						const value = productCharacteristic.value;
						if (value) {
							setCreditLimit(value);
						}
					}
				},
				(error) => throwError(error)
			);
	}, [phoneNumber, accountNumber, dispatch]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	return (
		<ConditionalRender
			show={!loading}
			onTrue={() => (
				<div className={classes.postpaidWrapper}>
					<Card className={classes.creditLimitCard}>
						<CardBody>
							<div>
								<HeadingSBold>
									{translate('numbers.details.credit.limit.value', creditLimit || '-')}
								</HeadingSBold>
								<BodyS>{translate('numbers.details.credit', MIN_CREDIT_LIMIT, MAX_CREDIT_LIMIT)}</BodyS>
							</div>
							<Button color="primary-light" onClick={() => setIsOpen(true)}>
								{translate('change')}
							</Button>
						</CardBody>
					</Card>
					<ServicesAccordion
						active={activeSection ?? 1}
						collapses={[
							{
								title: translate('number.services.mobile.gsm.services'),
								notificationDot:
									roamingInternationalPending || internationalPending
										? {
												color: 'danger',
										  }
										: isMobileServicesActive
										? {
												color: 'success',
										  }
										: undefined,
								content: (
									<MobileServicesContentWrapper
										internationalEnabled={internationalEnabled}
										showOnlyActive={showOnlyActive}
										updateServices={updateServices}
										roamingInternationalEnabled={roamingInternationalEnabled}
										roamingInternationalPending={roamingInternationalPending}
										internationalPending={internationalPending}
										pendingStatusDate={roamingInternationalPendingStatusDate}
										setIsMobileServicesActive={setIsMobileServicesActive}
									/>
								),
							},
							{
								title: translate('number.services.mobile.internet.plans'),
								notificationDot: pendingStatuses.mobileInternetPlans
									? {
											color: 'danger',
									  }
									: isMobileInternetPlansActive
									? {
											color: 'success',
									  }
									: undefined,
								content: (
									<MobileInternetPlansContentWrapper
										subscriberProducts={subscriberProducts}
										updateServices={updateServices}
										defaultPayG={defaultPayG}
										setIsMobileInternetPlansActive={setIsMobileInternetPlansActive}
									/>
								),
							},
							{
								title: translate('number.services.CPN'),
								notificationDot: isCpnActive
									? {
											color: 'success',
									  }
									: undefined,
								content: (
									<CpnAccordionContentWrapper
										showOnlyActive={showOnlyActive}
										termsAndConditions={{
											type: 'link',
											url: apiConfig.links.cpn,
										}}
										phoneNumber={phoneNumber}
										accountDetails={accountDetails}
										accountNumber={accountNumber}
										setIsCpnActive={setIsCpnActive}
									/>
								),
							},
							{
								title: translate('number.services.gobiz'),
								notificationDot: pendingStatuses.gobiz
									? {
											color: 'danger',
									  }
									: isGoBizActive
									? {
											color: 'success',
									  }
									: undefined,
								content: (
									<GobizAccordionContentWrapper
										termsAndConditions={{
											type: 'link',
											url: apiConfig.links.roamingPlans,
										}}
										updateOrderItems={updateOrderItems}
										subscriberProducts={subscriberProducts}
										roamingEnabled={roamingEnabled}
										packages={packages}
										roamingPending={roamingPending}
										defaultRoamingPayG={defaultRoamingPayG}
										setPendingStatus={(status: boolean) => setPendingStatus(status, 'gobiz')}
										setIsGoBizActive={setIsGoBizActive}
									/>
								),
							},
							{
								title: translate('number.services.mobile.jawazak.roaming'),
								notificationDot: pendingStatuses.jawazak
									? {
											color: 'danger',
									  }
									: isJawazakActive
									? {
											color: 'success',
									  }
									: undefined,
								content: (
									<JawazakContentWrapper
										showOnlyActive={showOnlyActive}
										updateOrderItems={updateOrderItems}
										roamingEnabled={roamingEnabled}
										subscriberProducts={subscriberProducts}
										packages={packages}
										roamingPending={roamingPending}
										setPendingStatus={(status: boolean) => setPendingStatus(status, 'jawazak')}
										termsAndConditions={{
											type: 'link',
											url: apiConfig.links.roamingPlans,
										}}
										setIsJawazakActive={setIsJawazakActive}
									/>
								),
							},
							{
								title: translate('services.gsm.international.minutes'),
								notificationDot: pendingStatuses.internationalMinutes
									? {
											color: 'danger',
									  }
									: isInternationalMinutesActive
									? {
											color: 'success',
									  }
									: undefined,
								content: (
									<InternationalMinutesAccordionWrapper
										termsAndConditions={{
											type: 'tooltip',
											content: (
												<div>
													<HeadingSBold>
														{translate('number.services.terms.conditions')}
													</HeadingSBold>
													<Caption disableMargin>
														{translate('services.international.conditions.first.part')}
													</Caption>
													<Caption disableMargin>
														{translate('services.international.conditions.second.part')}
													</Caption>
												</div>
											),
										}}
										updateOrderItems={updateOrderItems}
										internationalEnabled={internationalEnabled || roamingInternationalEnabled}
										packages={packages}
										subscriberProducts={subscriberProducts}
										setPendingStatus={(status: boolean) =>
											setPendingStatus(status, 'internationalMinutes')
										}
										internationalPending={internationalPending || roamingInternationalPending}
										orderStatuses={orderStatuses}
										setIsInternationalMinutesActive={setIsInternationalMinutesActive}
									/>
								),
							},
						].filter(Boolean)}
					/>
					<AuthConsumer>
						{({ user }) => (
							<ChangeCreditLimitDialog
								isOpen={isOpen}
								hideDialog={() => setIsOpen(false)}
								phoneNumber={phoneNumber}
								accountDetails={accountDetails}
								accountNumber={accountNumber}
								userData={userData}
								creditLimit={creditLimit}
								setCreditLimit={setCreditLimit}
								documentId={documentId}
								msisdn={user?.contactNumber1}
							/>
						)}
					</AuthConsumer>
				</div>
			)}
		/>
	);
};

export default MobilePostpaid;
